import {
    usePostServiceOrdersCompletionsMutation,
    usePostServiceOrdersStartInfosMutation,
} from '../../../api/serviceOrdersApi';
import { trimmedOrUndefined } from '../../../api/utils';
import { handleQueryError } from '../../../notifications/ErrorNotification';
import { showSuccessNotification } from '../../../notifications/SuccessNotification';
import { CollectedAccessories } from '../accessory.types';

export type SaveServiceOrderExecutionInput = {
    readonly orderIds: string[];
    readonly startedAt: Date | undefined;
    readonly completedAt: Date | undefined;
    readonly generalInformation: string | undefined;
    readonly collectedAccessories?: CollectedAccessories | undefined;
};

export const useSaveServiceOrderExecution = () => {
    const [updateStartInfo, startInfoRequest] = usePostServiceOrdersStartInfosMutation();
    const [updateCompletion, completionRequest] = usePostServiceOrdersCompletionsMutation();

    const saveExecution = async ({
        orderIds,
        startedAt,
        completedAt,
        generalInformation,
        collectedAccessories,
    }: SaveServiceOrderExecutionInput): Promise<boolean> => {
        const commonPayload = {
            orderIds: new Set(orderIds),
            generalInformation: trimmedOrUndefined(generalInformation),
            collectedAccessories,
        };

        try {
            if (completedAt === undefined) {
                if (startedAt === undefined) {
                    throw new Error('A start date is required when updating service order start information');
                }

                await updateStartInfo({ ...commonPayload, startedAt }).unwrap();
                showSuccessNotification('outboundPortal.notification.serviceOrder.start.info');
            } else {
                await updateCompletion({ ...commonPayload, startedAt, completedAt }).unwrap();
                showSuccessNotification('outboundPortal.notification.serviceOrder.completion.success');
            }

            return true;
        } catch (error) {
            handleQueryError(error);
            return false;
        }
    };

    return {
        saveExecution,
        isLoading: startInfoRequest.isLoading || completionRequest.isLoading,
    };
};
